import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ImagePlus, X, Loader2, Send, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Chapter } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = [
  "Birthday", "Wedding", "Baby Shower", "Bridal Shower",
  "Anniversary", "Graduation", "Corporate Event", "Other",
];
const ITEMS = ["Custom Cake", "Cupcakes", "Macarons", "Cake + Desserts"];
const SIZES = ["4-inch round", "6-inch round", "8-inch round", "Not Sure / Help Me Choose"];
const CAKE_FLAVORS = [
  "Chocolate", "Vanilla", "Strawberry", "Ube", "Mocha / Espresso",
  "Lemon", "Red Velvet", "Confetti", "Pistachio", "Matcha",
];
const BUTTERCREAM = ["Vanilla", "Chocolate", "Strawberry", "Lemon", "Mocha", "Matcha", "Cream Cheese"];
const BUDGETS = ["Under $100", "$100\u2013$150", "$150\u2013$250", "$250+", "Not Sure"];

const INITIAL = {
  first_name: "", last_name: "", email: "", phone: "",
  event_date: "", event_type: "", items: [], cake_size: "",
  cake_flavor: "", buttercream_flavor: "", design_details: "",
  budget: "", additional_info: "",
};

const fieldCls =
  "rounded-xl border-[#E8D8D3] bg-white text-[#2D2422] placeholder:text-[#8A7975]/60 focus-visible:ring-[#C48B71]/40 focus-visible:border-[#C48B71]";

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-2">
    <Label htmlFor={htmlFor} className="font-accent text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C4B47]">
      {label}
    </Label>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="font-display text-2xl font-normal text-[#2D2422]">{children}</h3>
);

const InquiryForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const toggleItem = (item) =>
    setForm((f) => ({
      ...f,
      items: f.items.includes(item) ? f.items.filter((i) => i !== item) : [...f.items, item],
    }));

  const addFiles = (list) => {
    const next = [...files, ...Array.from(list)].slice(0, 6);
    setFiles(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      let paths = [];
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const up = await axios.post(`${API}/uploads`, fd);
        paths = up.data.paths;
      }
      await axios.post(`${API}/inquiries`, { ...form, inspiration_paths: paths });
      setSubmitted(true);
      toast.success("Your cake request has been sent");
      scrollToFormTop();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToFormTop = () => {
    const el = document.querySelector("#inquiry");
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="inquiry" className="scroll-mt-24 bg-[#F4EFEB] py-24 sm:py-32" data-testid="inquiry-section">
      <div className="mx-auto max-w-4xl px-5 sm:px-8">
        <Reveal>
          <Chapter num="06" label="Cake Inquiry" />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-4xl font-light leading-tight tracking-tight text-[#2D2422] sm:text-5xl">
            Tell Us About Your Cake
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5C4B47]">
            We&rsquo;d love to create something special for your celebration. Tell us a little about
            what you have in mind and we&rsquo;ll get back to you with availability and pricing.
          </p>
        </Reveal>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 rounded-3xl border border-[#E8D8D3] bg-white p-10 text-center sm:p-14"
            data-testid="form-success-message"
          >
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#E8D8D3]/60 text-[#C48B71]">
              <PartyPopper size={26} strokeWidth={1.5} />
            </span>
            <h3 className="mt-6 font-display text-3xl font-light text-[#2D2422]">Thank you!</h3>
            <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#5C4B47]">
              We received your request and will review your event details, availability, and design
              ideas before reaching out with next steps.
            </p>
            <p className="mx-auto mt-4 max-w-md font-display text-lg italic text-[#8A7975]">
              Submitting this form does not automatically confirm your order or reserve your date.
            </p>
            <button
              onClick={() => { setSubmitted(false); setForm(INITIAL); setFiles([]); }}
              className="btn-outline mt-8"
              data-testid="form-submit-another-btn"
            >
              Submit Another Request
            </button>
          </motion.div>
        ) : (
          <Reveal delay={0.2}>
            <form
              onSubmit={handleSubmit}
              className="mt-12 space-y-12 rounded-3xl border border-[#E8D8D3] bg-white p-6 sm:p-10"
              data-testid="inquiry-form"
            >
              <div className="space-y-6">
                <SectionTitle>Contact Information</SectionTitle>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="First Name" htmlFor="first-name">
                    <Input id="first-name" required value={form.first_name} onChange={set("first_name")}
                      className={fieldCls} data-testid="first-name-input" placeholder="Bella" />
                  </Field>
                  <Field label="Last Name" htmlFor="last-name">
                    <Input id="last-name" required value={form.last_name} onChange={set("last_name")}
                      className={fieldCls} data-testid="last-name-input" placeholder="Ross" />
                  </Field>
                  <Field label="Email Address" htmlFor="email">
                    <Input id="email" type="email" required value={form.email} onChange={set("email")}
                      className={fieldCls} data-testid="email-input" placeholder="you@email.com" />
                  </Field>
                  <Field label="Phone Number" htmlFor="phone">
                    <Input id="phone" type="tel" required value={form.phone} onChange={set("phone")}
                      className={fieldCls} data-testid="phone-input" placeholder="(619) 555-0123" />
                  </Field>
                </div>
              </div>

              <div className="space-y-6">
                <SectionTitle>Event Information</SectionTitle>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Event Date" htmlFor="event-date">
                    <Input id="event-date" type="date" value={form.event_date} onChange={set("event_date")}
                      className={fieldCls} data-testid="event-date-input" />
                  </Field>
                  <Field label="Event Type">
                    <Select value={form.event_type} onValueChange={set("event_type")}>
                      <SelectTrigger className={fieldCls} data-testid="event-type-select">
                        <SelectValue placeholder="Select event type" />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map((t) => (
                          <SelectItem key={t} value={t} data-testid={`event-type-option-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>

              <div className="space-y-6">
                <SectionTitle>What Would You Like?</SectionTitle>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="items-checkbox-group">
                  {ITEMS.map((item) => (
                    <label
                      key={item}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors duration-300 ${
                        form.items.includes(item)
                          ? "border-[#C48B71] bg-[#C48B71]/5"
                          : "border-[#E8D8D3] hover:border-[#C48B71]/50"
                      }`}
                      data-testid={`item-checkbox-${item.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      <Checkbox
                        checked={form.items.includes(item)}
                        onCheckedChange={() => toggleItem(item)}
                        className="border-[#C48B71] data-[state=checked]:bg-[#C48B71]"
                      />
                      <span className="text-sm text-[#2D2422]">{item}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <SectionTitle>Cake Details</SectionTitle>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <Field label="Cake Size">
                    <Select value={form.cake_size} onValueChange={set("cake_size")}>
                      <SelectTrigger className={fieldCls} data-testid="cake-size-select">
                        <SelectValue placeholder="Select a size" />
                      </SelectTrigger>
                      <SelectContent>
                        {SIZES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`cake-size-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Cake Flavor">
                    <Select value={form.cake_flavor} onValueChange={set("cake_flavor")}>
                      <SelectTrigger className={fieldCls} data-testid="cake-flavor-select">
                        <SelectValue placeholder="Select a flavor" />
                      </SelectTrigger>
                      <SelectContent>
                        {CAKE_FLAVORS.map((fl) => (
                          <SelectItem key={fl} value={fl} data-testid={`cake-flavor-option-${fl.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{fl}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Buttercream Flavor">
                    <Select value={form.buttercream_flavor} onValueChange={set("buttercream_flavor")}>
                      <SelectTrigger className={fieldCls} data-testid="buttercream-flavor-select">
                        <SelectValue placeholder="Select a buttercream" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUTTERCREAM.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`buttercream-option-${b.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Budget (Optional)">
                    <Select value={form.budget} onValueChange={set("budget")}>
                      <SelectTrigger className={fieldCls} data-testid="budget-select">
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGETS.map((b) => (
                          <SelectItem key={b} value={b} data-testid={`budget-option-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </div>

              <div className="space-y-6">
                <SectionTitle>Design Details</SectionTitle>
                <Field label="Tell us about your vision" htmlFor="design-details">
                  <Textarea
                    id="design-details"
                    rows={6}
                    value={form.design_details}
                    onChange={set("design_details")}
                    className={fieldCls}
                    data-testid="design-details-textarea"
                    placeholder="Theme, colors, style, writing or text for the cake, decorations, number of guests, and any other special requests..."
                  />
                </Field>

                <div className="space-y-3">
                  <span className="font-accent text-[11px] font-bold uppercase tracking-[0.2em] text-[#5C4B47]">
                    Upload Your Inspiration
                  </span>
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex w-full flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-[#C48B71]/50 bg-[#FDFBF7] px-6 py-10 text-center transition-colors duration-300 hover:border-[#C48B71] hover:bg-[#C48B71]/5"
                    data-testid="inspiration-upload-btn"
                  >
                    <ImagePlus size={26} strokeWidth={1.25} className="text-[#C48B71]" />
                    <span className="text-sm text-[#5C4B47]">
                      Tap to add up to 6 inspiration photos
                    </span>
                    <span className="max-w-md text-xs leading-relaxed text-[#8A7975]">
                      Inspiration photos help us understand your style. Your final cake will be a
                      custom Bella Bells Cakes creation rather than an exact copy of another
                      baker&rsquo;s work.
                    </span>
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid="inspiration-upload-input"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                  />
                  {files.length > 0 && (
                    <div className="flex flex-wrap gap-3 pt-2" data-testid="inspiration-preview-list">
                      {files.map((f, i) => (
                        <div key={i} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-[#E8D8D3]">
                          <img src={URL.createObjectURL(f)} alt="" className="h-full w-full object-cover" />
                          <button
                            type="button"
                            onClick={() => setFiles(files.filter((_, j) => j !== i))}
                            className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-[#2D2422] opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                            aria-label="Remove photo"
                            data-testid={`inspiration-remove-${i}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <SectionTitle>Additional Information</SectionTitle>
                <Field label="Anything else we should know? (Optional)" htmlFor="additional-info">
                  <Textarea
                    id="additional-info"
                    rows={4}
                    value={form.additional_info}
                    onChange={set("additional_info")}
                    className={fieldCls}
                    data-testid="additional-info-textarea"
                    placeholder="Allergies, delivery questions, timing, or anything else on your mind..."
                  />
                </Field>
              </div>

              <div className="space-y-4 border-t border-[#E8D8D3] pt-8">
                <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-60 sm:w-auto" data-testid="inquiry-submit-btn">
                  {submitting ? (
                    <>Sending <Loader2 size={16} className="animate-spin" /></>
                  ) : (
                    <>Send My Cake Request <Send size={15} strokeWidth={1.5} /></>
                  )}
                </button>
                <p className="text-xs leading-relaxed text-[#8A7975]" data-testid="inquiry-disclaimer">
                  Submitting this form does not automatically confirm your order or reserve your
                  date. Bella Bells Cakes will personally review your request and follow up with
                  availability, final pricing, and next steps.
                </p>
              </div>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export default InquiryForm;
